import React from 'react';
import { ArrowUpRight, Plus } from 'lucide-react';

// CONCEPT 3 — Immersive Product Studio & Workflow
const PreviewConcept3 = () => {
  return (
    <main className="pt-20 pb-24 min-h-screen" style={{ backgroundColor: '#FAFAFA' }} data-testid="preview-c3">
      {/* Concept banner */}
      <div className="fixed top-20 right-6 z-40 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-lg">
        <span className="text-[10px] uppercase tracking-widest font-mono text-[#FF4E50] font-bold">Preview · Concept 3</span>
      </div>

      {/* Registration marks corners */}
      <div className="fixed top-24 left-6 opacity-40">
        <svg width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="4" fill="none" stroke="#FF4E50" strokeWidth="1"/><line x1="0" y1="10" x2="20" y2="10" stroke="#FF4E50" strokeWidth="1"/><line x1="10" y1="0" x2="10" y2="20" stroke="#FF4E50" strokeWidth="1"/></svg>
      </div>

      {/* Editorial masthead */}
      <section className="max-w-[1440px] mx-auto px-10 pt-10">
        <div className="flex items-center justify-between pb-8 border-b border-slate-900/10">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-500">Vol. I · Issue 02</span>
            <span className="h-1 w-1 rounded-full bg-slate-400" />
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-500">Feb 2026</span>
          </div>
          <div className="flex items-center gap-6 text-[11px] font-mono uppercase tracking-[0.3em] text-slate-500">
            <span>Barbieri</span><span>·</span><span>Nix</span><span>·</span><span>PrintFactory</span><span>·</span><span>ImpoProof</span>
          </div>
        </div>

        {/* Hero editorial */}
        <div className="grid grid-cols-12 gap-10 pt-16">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF4E50] mb-6">Feature · The Studio</p>
            <h1 className="text-6xl lg:text-[5.5rem] font-black leading-[0.9] tracking-[-0.035em] text-slate-900">
              Hardware<br/>
              <em className="italic font-light text-slate-500">+ software</em><br/>
              for enterprise<br/>
              print accuracy.
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-md">
              The tactile side of color science. Studio-grade instruments, cinematographic product craft, and end-to-end pressroom workflows — hand-selected for North American print leaders.
            </p>
            <div className="mt-10 flex items-center gap-5">
              <button className="group inline-flex items-center gap-2 text-slate-900 text-sm font-bold uppercase tracking-widest border-b-2 border-slate-900 pb-1 hover:text-[#FF4E50] hover:border-[#FF4E50] transition">
                Book a studio tour <ArrowUpRight className="h-4 w-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
              </button>
              <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">30-min · virtual · no obligation</span>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <div className="aspect-[4/5] w-full relative rounded-none overflow-hidden bg-slate-100">
              <img src="/nix/spectro2-lifestyle.jpg" alt="Nix Spectro 2 pressroom" className="w-full h-full object-cover" />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-900">
                Fig. 01 — Nix Spectro 2 · Q4 press cycle
              </div>
              <div className="absolute bottom-6 right-6 bg-slate-900 text-white px-4 py-3 max-w-[220px]">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/60 mb-1">Sample metric</p>
                <p className="text-2xl font-black">ΔE 4.8 → 0.4</p>
                <p className="text-[10px] text-white/70 mt-1">Pre / post G7 calibration on brand-critical Pantone target.</p>
              </div>
            </div>
            <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.3em] text-slate-500">
              Photographed on-site · Pinnacle Intertech, MI
            </p>
          </div>
        </div>
      </section>

      {/* Workflow pillars — big numbered rows */}
      <section className="max-w-[1440px] mx-auto px-10 mt-32">
        <div className="flex items-end justify-between pb-6 border-b border-slate-900/10">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF4E50]">Chapter II</p>
            <h2 className="text-4xl lg:text-6xl font-black text-slate-900 mt-4 leading-none tracking-[-0.02em]">The Workflow.</h2>
          </div>
          <p className="text-slate-500 max-w-sm text-sm leading-relaxed hidden md:block">Four pillars, one calibrated color signal — measured, RIP&apos;d, imposed and verified.</p>
        </div>

        <div className="grid grid-cols-12 gap-6 mt-10">
          {[
            { n: '01', t: 'Measure', desc: 'Barbieri Spectro & Nix Spectro capture reference-grade spectral data at the press.', tag: 'Hardware' },
            { n: '02', t: 'RIP', desc: 'PrintFactory transforms it into a device-independent color signal.', tag: 'Software' },
            { n: '03', t: 'Impose', desc: 'ImpoProof lays out sheets with imposition-aware color proofing.', tag: 'Software' },
            { n: '04', t: 'Certify', desc: 'On-site G7 & ISO 12647 calibration, delivered by ProofNation techs.', tag: 'Service' },
          ].map(p => (
            <div key={p.n} className="col-span-12 md:col-span-6 lg:col-span-3 group cursor-pointer">
              <div className="aspect-[3/4] bg-white border border-slate-200 relative overflow-hidden hover:border-slate-900 transition">
                <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">{p.n}</span>
                <span className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-widest text-[#FF4E50] bg-[#FF4E50]/10 px-2 py-0.5">{p.tag}</span>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-4xl font-black text-slate-900 leading-none tracking-tight">{p.t}.</h3>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{p.desc}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-[11px] font-mono uppercase tracking-widest text-slate-900 border-b border-slate-900 pb-0.5 group-hover:text-[#FF4E50] group-hover:border-[#FF4E50] transition">
                    <Plus className="h-3 w-3" /> Read chapter
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Before / After ΔE slider signature moment */}
      <section className="max-w-[1440px] mx-auto px-10 mt-32">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF4E50] mb-4">Signature moment</p>
        <h2 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-[-0.02em]">
          The before / after that sells the contract.
        </h2>
        <div className="mt-10 grid grid-cols-2 gap-0 border border-slate-900">
          <div className="relative aspect-[3/2] overflow-hidden">
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #b45f4f, #8a6a4d, #5a4f3b)', filter: 'saturate(0.4) blur(0.5px)' }} />
            <div className="absolute top-4 left-4 bg-white/95 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-900">Before · ΔE 4.8</div>
          </div>
          <div className="relative aspect-[3/2] overflow-hidden border-l border-slate-900">
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #d4462b, #c8541e, #a63a1c)' }} />
            <div className="absolute top-4 right-4 bg-slate-900 text-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest">After · ΔE 0.4</div>
          </div>
        </div>
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-500 mt-3">Fig. 02 — Pantone 1795 C on 250 gsm silk · G7 calibration cycle</p>
      </section>
    </main>
  );
};

export default PreviewConcept3;
